import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  EligibilityCheckResponse,
  OrganizedCoverages,
  StatusLight,
} from '@features/patient-coverages';
import {
  EligibilityStatusIcon,
  formatAlertLabel,
  getAlertGuidance,
  getBenefitExplainer,
  getCoverageOrderLabel,
  getFinancials,
  STATUS_CONFIG,
} from '@features/patient-coverages';
import { ExpandMore, WarningAmber } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  Divider,
  IconButton,
  Link,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { formatDistanceToNow, isPast, parseISO } from 'date-fns';

export interface EligibilityStatusBadgeProps {
  patientId?: string;
  encounterId?: string;
  source: 'patient_overview' | 'note_metadata';
  eligibilityData?: EligibilityCheckResponse;
  status?: StatusLight;
  organized: OrganizedCoverages;
  canRunCheck: boolean;
  runCheck: () => void;
  isRunning: boolean;
  mode: 'unchecked' | 'checked';
}

function formatTimestamp(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function isExpired(iso: string): boolean {
  try {
    return isPast(parseISO(iso));
  } catch {
    return false;
  }
}

function formatDollars(dollars: number): string {
  return `$${dollars.toFixed(2)}`;
}

export const EligibilityStatusBadge: React.FC<EligibilityStatusBadgeProps> = ({
  patientId,
  encounterId,
  source,
  eligibilityData,
  status,
  organized,
  canRunCheck,
  runCheck,
  isRunning,
  mode,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { eligibilityChecks } = useEventTracking();

  const [isOpen, setIsOpen] = useState(false);
  const [hasTrackedPopover, setHasTrackedPopover] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track badge view on mount
  useEffect(() => {
    eligibilityChecks.badgeViewed?.({
      patient_id: patientId,
      encounter_id: encounterId,
      source,
      mode,
      status,
    });
  }, [patientId, encounterId, source, mode, status, eligibilityChecks]);

  // Track popover opened
  useEffect(() => {
    if (isOpen && !hasTrackedPopover) {
      eligibilityChecks.badgePopoverOpened?.({
        patient_id: patientId,
        encounter_id: encounterId,
        source,
        mode,
        status,
      });
      setHasTrackedPopover(true);
    }
    if (!isOpen) {
      setHasTrackedPopover(false);
    }
  }, [isOpen, hasTrackedPopover, patientId, encounterId, source, mode, status, eligibilityChecks]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  }, []);

  const handleClickAway = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleRunCheck = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      eligibilityChecks.runCheckClicked?.({
        patient_id: patientId,
        encounter_id: encounterId,
        source,
      });
      runCheck();
    },
    [patientId, encounterId, source, runCheck, eligibilityChecks],
  );

  const handleViewDetails = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      eligibilityChecks.viewDetailsClicked?.({
        patient_id: patientId,
        encounter_id: encounterId,
        source,
        status,
      });
      setIsOpen(false);
      if (patientId) {
        navigate(`/patient/${patientId}`, {
          replace: true,
          state: { tab: 'billing', encounterId },
        });
      }
    },
    [patientId, encounterId, source, status, navigate, eligibilityChecks],
  );

  const handleLearnMore = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setIsOpen(false);
      navigate('/eligibility-checks');
    },
    [navigate],
  );

  // --- Icon ---
  const renderIcon = () => {
    if (mode === 'checked' && status) {
      return (
        <IconButton
          ref={anchorRef}
          size="small"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ p: 0.5, ml: 0.5 }}
          aria-label={`Eligibility: ${STATUS_CONFIG[status].label}`}
        >
          <EligibilityStatusIcon status={status} size={20} />
        </IconButton>
      );
    }

    // unchecked mode
    return (
      <IconButton
        ref={anchorRef}
        size="small"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          p: 0.5,
          ml: 0.5,
          color: theme.palette.warning.main,
          '&:hover': { bgcolor: 'action.hover' },
        }}
        aria-label="Eligibility not yet verified"
      >
        <WarningAmber fontSize="small" />
      </IconButton>
    );
  };

  // --- Popover content: checked ---
  const [moreDetailsOpen, setMoreDetailsOpen] = useState(false);

  const renderCheckedPopover = () => {
    if (!eligibilityData || !status) return null;
    const statusConfig = STATUS_CONFIG[status];
    const expired = eligibilityData.expires_at ? isExpired(eligibilityData.expires_at) : false;
    const fin = getFinancials(eligibilityData);

    const hasSecondaryDetails =
      fin.copayment != null ||
      fin.coinsurance != null ||
      fin.deductible != null ||
      fin.oop_max != null ||
      eligibilityData.plan_status != null ||
      eligibilityData.last_checked_at != null ||
      eligibilityData.expires_at != null;

    return (
      <Stack spacing={1.5}>
        {/* Primary: Coverage status */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <EligibilityStatusIcon status={status} size={18} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {statusConfig.label}
          </Typography>
        </Stack>

        {/* Primary: Member Obligation — the money line */}
        {fin.member_obligation != null && (
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              Est. Patient Responsibility
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {formatDollars(fin.member_obligation)}
            </Typography>
            {fin.benefit_structure && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {fin.benefit_structure}
              </Typography>
            )}
            {(() => {
              const explainer = getBenefitExplainer(fin);
              return explainer ? (
                <Typography variant="caption" sx={{ mt: 0.25, display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                  {explainer}
                </Typography>
              ) : null;
            })()}
          </Box>
        )}

        {/* Alerts — show actionable guidance */}
        {eligibilityData.alerts && eligibilityData.alerts.length > 0 && (
          <Stack spacing={0.75}>
            {eligibilityData.alerts.map(alert => {
              const guidance = getAlertGuidance(alert);
              return (
                <Typography key={alert} variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                  {guidance ?? formatAlertLabel(alert)}
                </Typography>
              );
            })}
          </Stack>
        )}

        {/* More details — collapsible */}
        {hasSecondaryDetails && (
          <>
            <Box
              component="button"
              onClick={() => setMoreDetailsOpen(prev => !prev)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                p: 0,
                color: 'primary.main',
                fontSize: '0.8125rem',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              More details
              <ExpandMore
                sx={{
                  fontSize: 16,
                  transition: 'transform 0.2s',
                  transform: moreDetailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Box>
            <Collapse in={moreDetailsOpen}>
              <Stack spacing={1} sx={{ pt: 0.5 }}>
                <Divider />
                {/* Cost breakdown */}
                {(fin.copayment != null || fin.coinsurance != null) && (
                  <Stack direction="row" spacing={2}>
                    {fin.copayment != null && (
                      <Typography variant="body2">
                        <Typography component="span" variant="body2" fontWeight={600}>
                          Copay:
                        </Typography>{' '}
                        {formatDollars(fin.copayment)}
                      </Typography>
                    )}
                    {fin.coinsurance != null && (
                      <Typography variant="body2">
                        <Typography component="span" variant="body2" fontWeight={600}>
                          Coinsurance:
                        </Typography>{' '}
                        {fin.coinsurance}%
                      </Typography>
                    )}
                  </Stack>
                )}
                {fin.deductible != null && (
                  <Stack direction="row" spacing={2}>
                    <Typography variant="body2">
                      <Typography component="span" variant="body2" fontWeight={600}>
                        Deductible:
                      </Typography>{' '}
                      {formatDollars(fin.deductible)}
                    </Typography>
                    {fin.remaining_deductible != null && (
                      <Typography variant="body2">
                        <Typography component="span" variant="body2" fontWeight={600}>
                          Remaining:
                        </Typography>{' '}
                        {formatDollars(fin.remaining_deductible)}
                      </Typography>
                    )}
                  </Stack>
                )}
                {fin.oop_max != null && (
                  <Stack direction="row" spacing={2}>
                    <Typography variant="body2">
                      <Typography component="span" variant="body2" fontWeight={600}>
                        OOP Max:
                      </Typography>{' '}
                      {formatDollars(fin.oop_max)}
                    </Typography>
                    {fin.remaining_oop_max != null && (
                      <Typography variant="body2">
                        <Typography component="span" variant="body2" fontWeight={600}>
                          Remaining:
                        </Typography>{' '}
                        {formatDollars(fin.remaining_oop_max)}
                      </Typography>
                    )}
                  </Stack>
                )}
                {/* Timestamps */}
                {eligibilityData.expires_at && (
                  <Tooltip title={`Expires: ${eligibilityData.expires_at}`}>
                    <Typography
                      variant="caption"
                      sx={{ color: expired ? 'error.main' : 'text.secondary' }}
                    >
                      {expired ? 'Expired' : `Expires ${formatTimestamp(eligibilityData.expires_at)}`}
                    </Typography>
                  </Tooltip>
                )}
                {eligibilityData.last_checked_at && (
                  <Typography variant="caption" color="text.secondary">
                    Last checked {formatTimestamp(eligibilityData.last_checked_at)}
                  </Typography>
                )}
              </Stack>
            </Collapse>
          </>
        )}

        {/* Review button for non-GREEN */}
        {status !== 'GREEN' && patientId && (
          <>
            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              {statusConfig.guidance}
            </Typography>
            <Button size="small" variant="outlined" onClick={handleViewDetails}>
              Review Eligibility Details
            </Button>
          </>
        )}
      </Stack>
    );
  };

  // --- Popover content: unchecked ---
  const renderUncheckedPopover = () => (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <WarningAmber
          sx={{ color: theme.palette.warning.main, mt: 0.25, flexShrink: 0 }}
          fontSize="small"
        />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Not yet verified
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Insurance is on file but eligibility has not been checked.
          </Typography>
        </Box>
      </Box>

      {/* Coverage info */}
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}
        >
          On File
        </Typography>
        {organized.primary && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {getCoverageOrderLabel(1)}: {organized.primary.payer_name || 'Unknown Payer'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {organized.primary.member_id
                ? `Member ID: ${organized.primary.member_id}`
                : 'No member ID'}
            </Typography>
          </Box>
        )}
        {organized.secondary && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {getCoverageOrderLabel(2)}: {organized.secondary.payer_name || 'Unknown Payer'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {organized.secondary.member_id
                ? `Member ID: ${organized.secondary.member_id}`
                : 'No member ID'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Divider />
      {canRunCheck && (
        <Button
          size="small"
          variant="outlined"
          onClick={handleRunCheck}
          disabled={isRunning}
          startIcon={isRunning ? <CircularProgress size={14} /> : undefined}
          fullWidth
          sx={{ textTransform: 'none' }}
        >
          {isRunning ? 'Checking...' : 'Run Eligibility Check'}
        </Button>
      )}
      <Link
        component="button"
        onClick={handleLearnMore}
        sx={{
          textAlign: 'left',
          textDecoration: 'none',
          color: theme.palette.primary.main,
          fontWeight: 500,
          fontSize: '0.875rem',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        Learn more about eligibility checks →
      </Link>
    </Stack>
  );

  return (
    <>
      {renderIcon()}

      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        sx={{ zIndex: theme.zIndex.tooltip }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={8}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
              p: 2,
              minWidth: 280,
              maxWidth: 360,
              borderRadius: 2,
            }}
          >
            {mode === 'checked' ? renderCheckedPopover() : renderUncheckedPopover()}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
